/*
 * File: useOpsBoardPaging.ts
 * Path: src/features/ops-board-v2/useOpsBoardPaging.ts
 * Description: Ops Board V2 Fit Engine for whole-row paging, stale-session display suppression, visible-day tallying, and end-of-day flip behavior.
 * Author: Larry McLean + ChatGPT
 * Created: 2026-07-12
 * Version: 1.0.0
 *
 * Change Log:
 * - 2026-07-12: v1.0.0 - Add Phase 2 Fit Engine paging module for measured whole-row kiosk pages.
 */

import React from 'react';
import {
  OpsBoardApiResponse,
  OpsBoardSession,
  OpsBoardSummary
} from './useOpsBoardV2Data';

export const END_OF_DAY_GRACE_MINUTES = 60;
export const PAGE_DWELL_MS = 12000;

type RowMeasurements = Record<string, number>;

type VisibleDayTally = {
  totalPax: number;
  pickupTotal: number;
  nitroxTotal: number;
  rentalGearTotal: number;
  waitlistPaxTotal: number;
};

type UseOpsBoardPagingArgs = {
  data: OpsBoardApiResponse | null;
  rowsRef: React.RefObject<HTMLDivElement | null>;
  measurementRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
};

type UseOpsBoardPagingResult = {
  visibleDate: string;
  visibleSessions: OpsBoardSession[];
  measurementSessions: OpsBoardSession[];
  visibleDayTally: VisibleDayTally;
};

function parseTime(value: string) {
  const time = new Date(value).getTime();

  return Number.isFinite(time) ? time : 0;
}

function getSummary(
  data: OpsBoardApiResponse,
  session: OpsBoardSession
): OpsBoardSummary | undefined {
  return data.session_summaries[String(session.id)];
}

function getGraceMinutes(data: OpsBoardApiResponse) {
  return data.operator.ops_board_grace_minutes ?? 30;
}

function isPastGrace(
  data: OpsBoardApiResponse,
  session: OpsBoardSession
) {
  const now = parseTime(data.generated_at);
  const startsAt = parseTime(session.starts_at);
  const graceMs = getGraceMinutes(data) * 60 * 1000;

  return now >= startsAt + graceMs;
}

function isPastEndOfDayGrace(
  data: OpsBoardApiResponse,
  session: OpsBoardSession
) {
  const now = parseTime(data.generated_at);
  const startsAt = parseTime(session.starts_at);

  return now >= startsAt + END_OF_DAY_GRACE_MINUTES * 60 * 1000;
}

function getOrderedDates(sessions: OpsBoardSession[]) {
  return Array.from(
    new Set(sessions.map((session) => session.session_date).filter(Boolean))
  ).sort();
}

function getSessionsForDate(
  sessions: OpsBoardSession[],
  sessionDate: string
) {
  return sessions
    .filter((session) => session.session_date === sessionDate)
    .slice()
    .sort((left, right) => parseTime(left.starts_at) - parseTime(right.starts_at));
}

function getVisibleDate(data: OpsBoardApiResponse) {
  const dates = getOrderedDates(data.sessions);
  const currentDate = data.window.start;
  const tomorrow = dates.find((date) => date > currentDate);
  const currentSessions = getSessionsForDate(data.sessions, currentDate);

  if (
    tomorrow &&
    currentSessions.length > 0 &&
    currentSessions.every((session) => isPastEndOfDayGrace(data, session))
  ) {
    return tomorrow;
  }

  return currentDate;
}

function recomputeVisibleDayTally(
  data: OpsBoardApiResponse,
  sessions: OpsBoardSession[]
): VisibleDayTally {
  return sessions.reduce(
    (tally, session) => {
      const summary = getSummary(data, session);

      return {
        totalPax: tally.totalPax + Number(summary?.confirmed_pax || 0),
        pickupTotal: tally.pickupTotal + Number(summary?.pickup_count || 0),
        nitroxTotal:
          tally.nitroxTotal + Number(summary?.nitrox_requested_count || 0),
        rentalGearTotal:
          tally.rentalGearTotal + Number(summary?.rental_gear_count || 0),
        waitlistPaxTotal:
          tally.waitlistPaxTotal + Number(summary?.waitlist_pax || 0)
      };
    },
    {
      totalPax: 0,
      pickupTotal: 0,
      nitroxTotal: 0,
      rentalGearTotal: 0,
      waitlistPaxTotal: 0
    }
  );
}

function measureRows(
  sessions: OpsBoardSession[],
  measurementRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>
): RowMeasurements {
  return sessions.reduce<RowMeasurements>((measurements, session) => {
    const element = measurementRefs.current[String(session.id)];

    measurements[String(session.id)] = element
      ? Math.ceil(element.getBoundingClientRect().height)
      : 0;

    return measurements;
  }, {});
}

function getAvailableHeight(rowsRef: React.RefObject<HTMLDivElement | null>) {
  const element = rowsRef.current;

  return element ? Math.floor(element.getBoundingClientRect().height) : 0;
}

function getTotalHeight(
  sessions: OpsBoardSession[],
  measurements: RowMeasurements
) {
  return sessions.reduce(
    (total, session) => total + (measurements[String(session.id)] || 0),
    0
  );
}

function dropStaleUntilFit(
  data: OpsBoardApiResponse,
  sessions: OpsBoardSession[],
  measurements: RowMeasurements,
  availableHeight: number
) {
  let remaining = sessions.slice();

  while (
    availableHeight > 0 &&
    getTotalHeight(remaining, measurements) > availableHeight
  ) {
    const droppable = remaining
      .filter((session) => isPastGrace(data, session))
      .sort((left, right) => parseTime(left.starts_at) - parseTime(right.starts_at));

    if (droppable.length === 0) {
      break;
    }

    const dropId = String(droppable[0].id);
    remaining = remaining.filter((session) => String(session.id) !== dropId);
  }

  return remaining;
}

function buildPages(
  sessions: OpsBoardSession[],
  measurements: RowMeasurements,
  availableHeight: number
) {
  if (availableHeight <= 0) {
    return [sessions];
  }

  const pages: OpsBoardSession[][] = [];
  let currentPage: OpsBoardSession[] = [];
  let currentHeight = 0;

  sessions.forEach((session) => {
    const rowHeight = measurements[String(session.id)] || 0;
    const wouldFit = currentPage.length === 0 || currentHeight + rowHeight <= availableHeight;

    if (!wouldFit) {
      pages.push(currentPage);
      currentPage = [session];
      currentHeight = rowHeight;
      return;
    }

    currentPage.push(session);
    currentHeight += rowHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages.length > 0 ? pages : [[]];
}

function buildContentSignature(
  data: OpsBoardApiResponse,
  sessions: OpsBoardSession[]
) {
  return sessions
    .map((session) => {
      const summary = getSummary(data, session);
      const rosterLength = Array.isArray(summary?.roster)
        ? summary?.roster?.length || 0
        : 0;

      return [
        session.id,
        rosterLength,
        session.capacity_remaining,
        isPastGrace(data, session) ? 'past-grace' : 'protected'
      ].join(':');
    })
    .join('|');
}

export function useOpsBoardPaging({
  data,
  rowsRef,
  measurementRefs
}: UseOpsBoardPagingArgs): UseOpsBoardPagingResult {
  const [pageIndex, setPageIndex] = React.useState(0);
  const lastSignatureRef = React.useRef('');

  const visibleDate = React.useMemo(
    () => (data ? getVisibleDate(data) : ''),
    [data]
  );

  const allVisibleDaySessions = React.useMemo(
    () => (data ? getSessionsForDate(data.sessions, visibleDate) : []),
    [data, visibleDate]
  );

  const [measureTick, setMeasureTick] = React.useState(0);

  React.useLayoutEffect(() => {
    setMeasureTick((value) => value + 1);
  }, [allVisibleDaySessions, rowsRef, measurementRefs]);

  const pages = React.useMemo(() => {
    if (!data) {
      return [[]];
    }

    const measurements = measureRows(allVisibleDaySessions, measurementRefs);
    const availableHeight = getAvailableHeight(rowsRef);
    const afterStalePressure = dropStaleUntilFit(
      data,
      allVisibleDaySessions,
      measurements,
      availableHeight
    );

    return buildPages(afterStalePressure, measurements, availableHeight);
  }, [data, allVisibleDaySessions, rowsRef, measurementRefs, measureTick]);

  const signature = React.useMemo(
    () => (data ? buildContentSignature(data, allVisibleDaySessions) : ''),
    [data, allVisibleDaySessions]
  );

  React.useEffect(() => {
    if (signature !== lastSignatureRef.current) {
      lastSignatureRef.current = signature;
      setPageIndex(0);
    }
  }, [signature]);

  React.useEffect(() => {
    if (pages.length <= 1) {
      return undefined;
    }

    const dwellTimer = window.setInterval(() => {
      setPageIndex((value) => (value + 1) % pages.length);
    }, PAGE_DWELL_MS);

    return () => {
      window.clearInterval(dwellTimer);
    };
  }, [pages.length]);

  const visibleSessions = pages[Math.min(pageIndex, pages.length - 1)] || [];
  const visibleDayTally = data
    ? recomputeVisibleDayTally(data, allVisibleDaySessions)
    : {
        totalPax: 0,
        pickupTotal: 0,
        nitroxTotal: 0,
        rentalGearTotal: 0,
        waitlistPaxTotal: 0
      };

  return {
    visibleDate,
    visibleSessions,
    measurementSessions: allVisibleDaySessions,
    visibleDayTally
  };
}